<template>
  <div class="details-step" data-test-id="details-step">
    <Typography variant="h-5">
      Start with the basics of your program. A title, description, and image.
    </Typography>

    <v-form ref="form">
      <div class="form-fields">
        <v-text-field
          outlined
          color="brandPrimary"
          label="Program Name*"
          hide-details="auto"
          :value="name"
          :rules="[
            (v) => !!v || 'A name is required',
            (v) => !v || v.length <= 100 || 'Name must be 100 characters or fewer',
          ]"
          @input="$emit('update:name', $event)"
        />

        <v-textarea
          outlined
          color="brandPrimary"
          label="Brief program description"
          rows="4"
          persistent-hint
          hint="Displays on the dashboard summary card if you make the card visible before the program starts (set in the TImeline step)"
          :value="description"
          @input="$emit('update:description', $event)"
        />

        <div class="banner-heading">
          <Typography variant="subtitle-1">Add a banner photo</Typography>
          <Typography variant="body-2">
            We suggest an image size of 904 x 509px (Or any image with a 16:9 aspect ratio).<br />
            JPG, PNG files supported. Max file size: 5mb
          </Typography>
        </div>

        <DragAndDropImageInput
          :file="image"
          :existing-image-url="imageURL"
          collect-alt-text
          :alt-text="imageAltText"
          :max-file-size="MAX_IMAGE_SIZE_BYTES"
          :alt-text-rules="altTextRules"
          :aspect-ratio-value="16 / 9"
          aspect-ratio-text="16 : 9"
          crop-image
          @file-selected="onFileSelected"
          @file-rejected="onFileRejected"
          @clear-existing-image="onClearExistingImage"
          @update:altText="$emit('update:imageAltText', $event)"
        />
      </div>
    </v-form>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import DragAndDropImageInput from "@/components/DragAndDropImageInput.vue";

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_ALT_TEXT_LENGTH = 255;

export default {
  name: "DetailsStep",
  components: {
    Typography,
    DragAndDropImageInput,
  },
  props: {
    name: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    image: {
      type: File,
      default: null,
    },
    imageURL: {
      type: String,
      default: null,
    },
    imageAltText: {
      type: String,
      default: "",
    },
  },
  emits: [
    "update:name",
    "update:description",
    "update:image",
    "update:imageURL",
    "update:imageAltText",
  ],
  data() {
    return {
      MAX_IMAGE_SIZE_BYTES,
      // Reason string ("size" | "type") when the drop zone rejects a file.
      // The drop zone shows its own inline error; we mirror it here so
      // validate() can block Continue while the rejection is still on screen.
      imageRejection: null,
      altTextRules: [
        (v) =>
          !v ||
          v.length <= MAX_ALT_TEXT_LENGTH ||
          `Alt text must be ${MAX_ALT_TEXT_LENGTH} characters or fewer`,
        (v) => !!v || "Alt text is required",
      ],
    };
  },
  methods: {
    onFileSelected(file) {
      if (file) this.imageRejection = null;
      this.$emit("update:image", file);
    },
    onFileRejected({ reason }) {
      this.imageRejection = reason;
    },
    onClearExistingImage() {
      this.imageRejection = null;
      this.$emit("update:imageURL", null);
      this.$emit("update:imageAltText", null);
    },
    // Defensive belt-and-suspenders in case a File slips past the drop zone.
    imageWithinSizeLimit() {
      return !this.image || this.image.size <= MAX_IMAGE_SIZE_BYTES;
    },
    validate() {
      const formValid = this.$refs.form.validate();
      return formValid && !this.imageRejection && this.imageWithinSizeLimit();
    },
  },
};
</script>

<style scoped>
.details-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.banner-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
</style>
